import React, { useEffect, useState } from "react";
import { loadImage } from "../../api/medecin";
import { IPatient } from "../../types/patient";
import Spinner from "../Spinner";
import { useAtom } from "jotai";
import { refreshAtomRequest } from "../../state/refreshRequestList";
import { getBodyPartLabel } from "../../config/bodyZones";
import { groupFilesByLesion, LesionGroup } from "../../helper/lesions";

const formatValue = (value: string | null | undefined) => {
  if (!value || typeof value !== "string") return ""
  return value.replace(/\b\w/g, (char) => char.toUpperCase())
}

interface IProps {
  helperCategorie: Function;
  setAddCategorieDocMenu: Function;
  addCategorieDocMenu: boolean;

  documentsList: [] | any;
  patientData: IPatient | any;
  setImageUrl: Function;
  setModalImage: Function;
  retriveDemandeMyAPI: Function;
  setDocumentsList: Function;
}

const parseImage = (specialite, userId, filePath) => ({
  original: loadImage(specialite, userId, "thumbnail", filePath),
});

const ImagesContainer: React.FC<IProps> = ({
  documentsList,
  patientData,
  setImageUrl,
  setModalImage,
  setDocumentsList,
}) => {
  const [parsedImages, setParsedImages] = useState<any[]>([]);
  const [refresh] = useAtom(refreshAtomRequest);
  const [loading, setLoading] = useState(false);


  const groupedByZone = React.useMemo(() => {
    const groups: { zone: string; lesions: LesionGroup<any>[] }[] = [];
    const groupIndexByZone: Record<string, number> = {};

    groupFilesByLesion(documentsList || []).forEach((lesion) => {
      const zoneKey = lesion.bodyPart || "Non spécifié";
      if (!(zoneKey in groupIndexByZone)) {
        groupIndexByZone[zoneKey] = groups.length;
        groups.push({ zone: zoneKey, lesions: [] });
      }
      groups[groupIndexByZone[zoneKey]].lesions.push(lesion);
    });

    return groups;
  }, [documentsList]);

  useEffect(() => {
    if (!documentsList?.length) return;

    Promise.all(
      documentsList.map(async (img) => ({
        original: await loadImage(
          img.specialite,
          img.patientId,
          "thumbnail",
          img.filePath
        ),
      }))
    ).then(setParsedImages);
  }, [documentsList, refresh?.state]);

  return loading ? (
    <Spinner />
  ) : (
    <>
      <div className="flex">
        <p className="doc-title">Images</p>
      </div>
      <div className="images flex flex-wrap min-w-full mt-2 gap-7">
        <div className="images flex flex-col min-w-full mt-2 gap-5">
          {parsedImages.length !== 0 ||
          patientData.status !== "WAIT_FOR_IMGS" ? (
            groupedByZone.map((group) => (
              <div className="zone-group w-full" key={group.zone}>
                <h4 className="zone-title text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">
                  {getBodyPartLabel(group.zone) || "Non spécifié"}
                </h4>
                <div className="flex flex-col gap-4">
                  {group.lesions.map((lesion, lesionIndex) => (
                    <div
                      className="border rounded-lg bg-white overflow-hidden p-4 sm:p-5 text-left w-full"
                      key={lesion.lesionId || lesionIndex}
                    >
                      <h3 className="text-base font-semibold text-gray-800 mb-3">
                        {group.lesions.length > 1
                          ? `Lésion ${lesionIndex + 1}`
                          : "Informations"}
                      </h3>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mb-4">
                        <div className="flex flex-col">
                          <span className="text-gray-500 font-medium">
                            Ancienneté
                          </span>
                          <span className="text-gray-800">
                            {formatValue(lesion.anciennete) || "Non spécifié"}
                          </span>
                        </div>

                        <div className="flex flex-col">
                          <span className="text-gray-500 font-medium">
                            Taille
                          </span>
                          <span className="text-gray-800">
                            {lesion.taille
                              ? `${lesion.taille} mm`
                              : "Non spécifié"}
                          </span>
                        </div>

                        <div className="flex flex-col">
                          <span className="text-gray-500 font-medium">
                            Douleur
                          </span>
                          <span className="text-gray-800">
                            {formatValue(lesion.douleur) || "Non spécifié"}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-4">
                        {lesion.items.map(({ file: doc, index: k }) => {
                          const i = parsedImages[k];
                          if (!i) return null;
                          return (
                            <div
                              className="flex flex-col w-40 md:w-48"
                              key={k}
                            >
                              <img
                                src={i?.original}
                                className="w-40 h-40 md:w-48 md:h-48 object-cover rounded-md cursor-pointer"
                                alt="document katomi"
                                onLoad={() => setLoading(false)}
                                onClick={() => {
                                  setImageUrl({
                                    url: i?.original,
                                    type: i?.original,
                                    fileInfo: doc,
                                  });

                                  setModalImage(true);
                                }}
                              />
                              <p className="text-sm text-gray-800 whitespace-pre-wrap break-words mt-2">
                                {doc?.message || (
                                  <span className="text-gray-400 italic">
                                    Pas de commentaire.
                                  </span>
                                )}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="flexCenter w-full anEmptyBox">
              <p className="card-subtitle">
                N'oublier pas de joindre les photos de votre patient afin de
                bénéficier de l'avis médical de nos experts en{" "}
                {patientData.specialite} .
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ImagesContainer;
